import { useCallback, useEffect, useState } from "react";
import { Loader2, RotateCcw, TrendingUp } from "lucide-react";
import { api, apiError, inr, shortDate } from "@/lib/api";
import { ListState, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

export default function LeadRoi() {
  const [data, setData] = useState(null);
  const [lost, setLost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, l] = await Promise.all([api.get("/lead-roi"), api.get("/lead-roi/lost")]);
      setData(r.data);
      setLost(l.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reactivate = async (lead) => {
    setBusy(lead.id);
    try {
      const { data: res } = await api.post(`/lead-roi/leads/${lead.id}/reactivate`);
      toast.success(res.note);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="lead-roi">
      <PageHeader
        testId="lead-roi-header"
        title="Lead Source ROI"
        subtitle={data?.best_source
          ? `${data.best_source} has brought in the most money so far`
          : "Which source actually turns into collected money"}
      />
      <div className="p-4 sm:p-6">
        {data && (
          <div className="surface mb-4 grid gap-3 p-4 sm:grid-cols-3 lg:grid-cols-6"
            data-testid="roi-totals">
            {[["Leads", data.totals.leads, false], ["Won", data.totals.won, false],
              ["Quoted", data.totals.quoted, true], ["Invoiced", data.totals.invoiced, true],
              ["Collected", data.totals.collected, true],
              ["Outstanding", data.totals.outstanding, true]].map(([k, v, rupee]) => (
              <div key={k}>
                <div className="overline">{k}</div>
                <div className="font-mono-num text-sm">{rupee ? inr(v) : v}</div>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="sources">
          <TabsList data-testid="roi-tabs">
            <TabsTrigger value="sources" data-testid="roi-tab-sources">By source</TabsTrigger>
            <TabsTrigger value="lost" data-testid="roi-tab-lost">
              Lost leads{lost?.total ? ` (${lost.total})` : ""}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sources">
            <ListState loading={loading} isEmpty={!data || data.rows.length === 0}
              emptyTitle="No leads yet" emptyHint="Add leads or import a list to see the ROI.">
              <div className="surface overflow-x-auto" data-testid="roi-table">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source</TableHead>
                      <TableHead className="text-right">Leads</TableHead>
                      <TableHead className="text-right">Won</TableHead>
                      <TableHead className="text-right">Win rate</TableHead>
                      <TableHead className="text-right">Quoted</TableHead>
                      <TableHead className="text-right">Invoiced</TableHead>
                      <TableHead className="text-right">Collected</TableHead>
                      <TableHead className="text-right">Outstanding</TableHead>
                      <TableHead className="text-right">Avg days to win</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data?.rows || []).map((r) => (
                      <TableRow key={r.source}
                        data-testid={`roi-row-${r.source.toLowerCase().replace(/\s+/g, "-")}`}>
                        <TableCell className="text-sm font-medium">
                          {r.source}
                          {r.open > 0 && (
                            <div className="text-[10px] text-muted-foreground">
                              {r.open} open • {inr(r.pipeline_value)} in pipeline
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono-num text-xs">{r.leads}</TableCell>
                        <TableCell className="text-right font-mono-num text-xs">{r.won}</TableCell>
                        <TableCell className="text-right text-xs">
                          <Badge variant={r.win_rate >= 40 ? "default" : "outline"}
                            className="text-[10px]">{r.win_rate}%</Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono-num text-xs">{inr(r.quoted)}</TableCell>
                        <TableCell className="text-right font-mono-num text-xs">{inr(r.invoiced)}</TableCell>
                        <TableCell className="text-right font-mono-num text-sm">{inr(r.collected)}</TableCell>
                        <TableCell className="text-right font-mono-num text-xs">{inr(r.outstanding)}</TableCell>
                        <TableCell className="text-right text-xs">
                          {r.avg_days_to_win ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </ListState>
            {data?.note && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="roi-note">
                {data.note}
              </p>
            )}
          </TabsContent>

          <TabsContent value="lost">
            <div className="mb-3 flex flex-wrap gap-2" data-testid="lost-reason-summary">
              {Object.entries(lost?.by_reason || {}).map(([k, v]) => (
                <Badge key={k} variant="outline" className="text-[10px]">{k}: {v}</Badge>
              ))}
            </div>
            <ListState loading={loading} isEmpty={false}
              emptyTitle="No lost leads" emptyHint="Marking a lead lost keeps its full history.">
              <div className="surface overflow-x-auto" data-testid="lost-leads-table">
                {(lost?.leads || []).length === 0 && (
                  <p className="p-4 text-sm text-muted-foreground" data-testid="lost-leads-empty">
                    No lost leads. Marking a lead lost keeps its full history and you can
                    reactivate it any time.
                  </p>
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lead</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Lost on</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(lost?.leads || []).map((l) => (
                      <TableRow key={l.id} data-testid={`lost-lead-${l.id}`}>
                        <TableCell className="text-sm">{l.title}</TableCell>
                        <TableCell className="text-xs">{l.source_label}</TableCell>
                        <TableCell className="text-xs">
                          {l.lost_reason || "Not recorded"}
                          {l.lost_remarks && (
                            <div className="text-[10px] text-muted-foreground">
                              {l.lost_remarks}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{shortDate(l.lost_at)}</TableCell>
                        <TableCell className="text-right font-mono-num text-xs">
                          {inr(l.estimated_value)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" disabled={busy === l.id}
                            data-testid={`reactivate-lead-${l.id}`}
                            onClick={() => reactivate(l)}>
                            <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reactivate
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </ListState>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

